import { type CraftButtonState, PromptForm } from '@/app/_common/prompt-form'
import { TurnMessages } from '@/app/chat/_common/turn-messages'
import { toast } from '@/components/ui/toaster'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import { allWorkflowsKeyFactory } from '@/hooks/api/use-all-workflows-query'
import { useSendMessageMutation } from '@/hooks/api/use-send-message-mutation'
import { Turn, turnStatusKeyFactory, useFetchTurnStatus } from '@/hooks/api/use-turn-status-query'
import { useTimeoutEffect } from '@/hooks/use-timeout-effect'
import { useApiRequest } from '@/lib/http-utils'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import { T } from '@tolgee/react'
import React from 'react'
import { useMeasure } from 'react-use'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'
import { useDomainFromUrl } from '../_common/domain/use-domain-from-url'
import { ErrorTabContent } from '../_common/split-view/tabs/content/_templates'
import { isTurnStatusLoading } from './_common/plan-steps/step-status'
import { useChatQueriesHelpers } from './_common/use-chat-data-queries-helpers'
import type { ChatData } from './_common/use-chat-data-query'
import {
  chatDataKeyFactory,
  handlePromptSubmitError,
  TOAST_ID_TOO_MANY_REQUESTS,
  useChatDataQuery,
} from './_common/use-chat-data-query'
import { ChatTurnEntry, usePlaceholderState } from './_common/use-turn-parsed'

function Chat({ workflowId }: Readonly<{ workflowId: string }>) {
  const [contentHeight, setContentHeight] = React.useState(0)
  const [paddingTop, setPaddingTop] = React.useState(0)
  const [craftButtonState, setCraftButtonState] = React.useState<CraftButtonState>('enabled')
  const [isLoading, setIsLoading] = React.useState(true)
  const [refScroller, { height: scrollAreaHeight }] = useMeasure<HTMLElement>()
  const { domainId } = useDomainFromUrl()
  const queryClient = useQueryClient()
  const refVirtuoso = React.useRef<VirtuosoHandle | null>(null)
  const refPromptTextarea = React.useRef<HTMLTextAreaElement | null>(null)
  const { setPlaceholder } = usePlaceholderState()
  const queryHelpers = useChatQueriesHelpers({ workflowId, setPlaceholder })

  // Track previous activeTurnId to detect when a turn completes
  const previousActiveTurnId = React.useRef<string | null>(null)

  // Direct query to get chat data instead of using context
  const {
    data: chatData,
    isLoading: isChatDataLoading,
    error: chatDataError,
  } = useChatDataQuery({
    workflowId,
    queryOptions: {
      staleTime: (query) => {
        const data = query.state.data as ChatData | undefined
        if (!data || !data.status) return 0
        // If the last turn is loading, data is stale
        if (isTurnStatusLoading(data.status)) return 0
        // Otherwise, data is fresh for a long time
        return Infinity
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  })

  const { mutate: cancelTurn } = useCancelTurnMutation({ workflowId })
  const { isPrefetching, hasInitialScroll, setHasInitialScroll } = useChatPrefetching({
    workflowId,
    chatData,
    isChatDataLoading,
    chatDataError,
  })

  const sendMessageMutation = useSendMessageMutation({
    onMutate: async ({ prompt, files }) => {
      toast.dismiss(TOAST_ID_TOO_MANY_REQUESTS)
      if (!workflowId) return { previousData: null }
      const { previous } = await queryHelpers.addPlaceholderTurn(prompt, files)
      return { previousData: previous }
    },
    onError: (error, data, context) => {
      console.error(error)
      handlePromptSubmitError(error?.message)

      if (context?.previousData && workflowId) {
        queryHelpers.setChatData(context.previousData)
      }
    },
    onSuccess: () => {
      if (workflowId) {
        queryHelpers.invalidateChat()
        queryClient.invalidateQueries({
          queryKey: allWorkflowsKeyFactory({
            domainId,
          }),
        })
      }
    },
  })

  // Calculate activeTurnId directly from chat data
  const activeTurnId = React.useMemo(() => {
    if (!chatData) return null

    const turns = chatData.turns
    const lastTurn = turns.findLast((t: ChatTurnEntry) => t.type === 'turn')

    if (lastTurn && isTurnStatusLoading(lastTurn.turn.status)) {
      return lastTurn.turnId
    }
    return null
  }, [chatData])

  const turnsSafe = chatData?.turns || []

  const showPromptForm = React.useMemo(
    () => Boolean(chatData?.prompt && typeof chatData?.prompt === 'string'),
    [chatData?.prompt],
  )

  const handleCancelTurn = React.useCallback(
    (turnId: string | null) => {
      if (turnId) {
        cancelTurn({ turnId })
        refPromptTextarea.current?.focus()
      } else {
        toast.error('Active turn not found')
      }
    },
    [cancelTurn],
  )

  const virtualItemContent = React.useCallback(
    (i: number, { turnId }: { turnId: string }) => {
      const isLastTurn = turnsSafe.length - 1 === i
      return (
        <TurnMessages
          key={turnId}
          workflowId={workflowId}
          turnId={turnId}
          chatTitle={chatData?.title || 'Chat'}
          activeTurnId={activeTurnId}
          style={isLastTurn ? { minHeight: scrollAreaHeight } : {}}
          className='mx-auto max-w-[784px] p-4'
        />
      )
    },
    [turnsSafe.length, workflowId, chatData?.title, activeTurnId, scrollAreaHeight],
  )

  React.useEffect(() => {
    if (scrollAreaHeight > 0) {
      const newPadding = Math.max(0, scrollAreaHeight - contentHeight)
      setPaddingTop((currentPadding) => {
        if (currentPadding !== newPadding) {
          return newPadding
        }
        return currentPadding
      })
    }
  }, [scrollAreaHeight, contentHeight])

  React.useEffect(() => {
    if (activeTurnId) {
      setCraftButtonState((prev) => (prev === 'loading' ? 'loading' : prev))
    } else {
      setCraftButtonState('enabled')
    }
  }, [activeTurnId])

  // Focus prompt textarea when a turn completes (activeTurnId changes from a value to null)
  React.useEffect(() => {
    if (previousActiveTurnId.current && !activeTurnId) {
      // A turn has completed, focus the textarea
      refPromptTextarea.current?.focus()
    }
    previousActiveTurnId.current = activeTurnId
  }, [activeTurnId])

  // Focus prompt textarea when workflow changes
  useTimeoutEffect(
    () => {
      refPromptTextarea.current?.focus()
    },
    Boolean(workflowId),
    [workflowId],
  )

  // Handle initial scroll to last turn after prefetching is complete
  useTimeoutEffect(
    () => {
      refVirtuoso.current?.scrollToIndex({
        index: 'LAST',
        align: 'start',
        behavior: 'auto',
      })
      setHasInitialScroll(true)
    },
    !isPrefetching && !isChatDataLoading && !hasInitialScroll && turnsSafe.length > 0,
    [isPrefetching, isChatDataLoading, hasInitialScroll, turnsSafe.length, setHasInitialScroll],
    150,
  )

  // Handle scroll to last turn when new turns are added
  useTimeoutEffect(
    () => {
      refVirtuoso.current?.scrollToIndex({
        index: 'LAST',
        align: 'start',
        behavior: 'smooth',
      })
    },
    hasInitialScroll && turnsSafe.length > 0,
    [turnsSafe.length, hasInitialScroll],
  )

  React.useEffect(() => {
    if (isPrefetching || isChatDataLoading) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [isPrefetching, isChatDataLoading])

  if (chatDataError) {
    return <ErrorTabContent />
  }

  return (
    <div className='flex h-full w-full flex-col items-center'>
      <div className='h-full w-full'>
        {!isLoading && (
          <Virtuoso<ChatTurnEntry>
            itemContent={virtualItemContent}
            scrollerRef={(el) => {
              if (el instanceof HTMLElement) {
                refScroller(el)
              }
            }}
            style={{ height: '100%' }}
            initialTopMostItemIndex={{ index: 'LAST', align: 'start' }}
            ref={refVirtuoso}
            data={turnsSafe}
            computeItemKey={(i, item) => item.turnId || i}
            increaseViewportBy={{ top: 800, bottom: 250 }}
            totalListHeightChanged={setContentHeight}
            components={{
              Header: () => <div style={{ paddingTop }} />,
            }}
          />
        )}
      </div>
      {showPromptForm && (
        <div className='w-full max-w-[784px] px-4'>
          <PromptForm
            clearOnSubmit
            ref={refPromptTextarea}
            activeTurnId={activeTurnId}
            onCancelTurn={handleCancelTurn}
            fieldId={workflowId}
            disableSubmit={sendMessageMutation.isPending || Boolean(activeTurnId)}
            renderFooter={() => (
              <p className='mt-4 text-center text-xs font-light text-neutral-500'>
                <T keyName='chat.promptTextarea.disclaimerV2' />
              </p>
            )}
            onSubmit={({ prompt, files }) => {
              setCraftButtonState('disabled')
              sendMessageMutation.mutate({ workflowId, prompt, files })
            }}
            craftButtonState={craftButtonState}
            onCraftAgentClick={() => {
              setCraftButtonState('loading')
              sendMessageMutation.mutate({
                workflowId,
                prompt: 'Craft an agent out of this conversation.',
                files: null,
              })
            }}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Hook to prefetch all turn data using useQueries for concurrent fetching
 */
function useChatPrefetching({
  workflowId,
  chatData,
  isChatDataLoading,
  chatDataError,
}: {
  workflowId: string
  chatData: ChatData | undefined
  isChatDataLoading: boolean
  chatDataError: unknown
}) {
  const { domainId } = useDomainFromUrl()
  const fetchTurnStatus = useFetchTurnStatus()
  const [isPrefetching, setIsPrefetching] = React.useState(true)
  const [hasInitialScroll, setHasInitialScroll] = React.useState(false)

  // Get turn IDs from chat data when available
  const turnIds = React.useMemo(() => {
    if (!chatData) return []
    return (
      chatData.turns
        ?.filter((entry): entry is ChatTurnEntry & { type: 'turn' } => entry.type === 'turn')
        .map((entry) => entry.turnId) || []
    )
  }, [chatData])

  // Create queries for all turns using useQueries
  const turnQueries = useQueries({
    queries: turnIds.map((turnId) => {
      // Get the current turn data to check if it's still loading
      const currentTurn = chatData
        ? (chatData.turns.find(
            (entry: ChatTurnEntry) => entry.type === 'turn' && entry.turnId === turnId,
          ) as (ChatTurnEntry & { type: 'turn' }) | undefined)
        : undefined

      const isTurnLoading = currentTurn ? isTurnStatusLoading(currentTurn.turn.status) : true

      return {
        queryKey: turnStatusKeyFactory({ workflowId, turnId, domainId }),
        queryFn: () => fetchTurnStatus({ workflowId, turnId }),
        enabled: chatData && isPrefetching && Boolean(workflowId) && Boolean(turnId),
        staleTime: isTurnLoading ? 0 : Infinity,
        refetchOnWindowFocus: false,
      }
    }),
  })

  // Reset prefetching state when workflowId changes
  React.useEffect(() => {
    setIsPrefetching(true)
  }, [workflowId])

  // Monitor prefetching completion
  React.useEffect(() => {
    if (chatData && isPrefetching && turnIds.length > 0) {
      // Check if all queries have completed (either success or error)
      const allQueriesCompleted = turnQueries.every((query) => query.isSuccess || query.isError)

      if (allQueriesCompleted) {
        setIsPrefetching(false)
      }
    } else if (chatDataError) {
      // If chat data failed to load, stop prefetching
      setIsPrefetching(false)
    }
  }, [chatData, isPrefetching, turnIds.length, turnQueries, chatDataError])

  return {
    isPrefetching: isPrefetching || isChatDataLoading,
    hasInitialScroll,
    setHasInitialScroll,
  }
}

function useCancelTurnMutation({ workflowId }: { workflowId: string }) {
  const queryClient = useQueryClient()
  const { apiRequest } = useApiRequest()
  const { domainId } = useDomainFromUrl()
  const { setPlaceholder } = usePlaceholderState()
  const chatQueriesHelpers = useChatQueriesHelpers({ workflowId, setPlaceholder })
  const { setTurnData, getChatData, setChatData } = chatQueriesHelpers || {}

  const chatDataQueryKey = chatDataKeyFactory({ workflowId, domainId })

  return useMutation({
    mutationKey: ['cancel-turn'],

    mutationFn: async ({ turnId }: { turnId: string }) => {
      const response = await apiRequest({
        path: taskManagerApiPaths.cancelTurn({ workflowId, turnId, domainId }),
        options: { method: 'POST' },
      })

      if (!response.ok) throw new Error('FAILED_TO_CANCEL_TURN')
    },

    onMutate: async ({ turnId }) => {
      const turnKey = turnStatusKeyFactory({ workflowId, turnId, domainId })

      // Cancel any ongoing queries
      await Promise.all([
        queryClient.cancelQueries({ queryKey: chatDataQueryKey }),
        queryClient.cancelQueries({ queryKey: turnKey }),
      ])

      // Snapshot current state
      const previousChatData = getChatData?.()
      const previousTurn = queryClient.getQueryData<Turn>(turnKey)

      // Optimistically update turn status using helpers
      if (previousTurn && setTurnData) {
        const cancelledTurn = {
          ...previousTurn,
          status: 'CANCELLED' as const,
          steps:
            previousTurn.steps?.map((step) => ({
              ...step,
              status: 'CANCELLED' as const,
            })) ?? [],
        }
        setTurnData(cancelledTurn)
      }

      // Set the query data directly with a timestamp to ensure it takes precedence
      if (previousTurn) {
        const cancelledTurn = {
          ...previousTurn,
          status: 'CANCELLED' as const,
          steps:
            previousTurn.steps?.map((step) => ({
              ...step,
              status: 'CANCELLED' as const,
            })) ?? [],
        }

        // Set the query data with a future timestamp to ensure it's considered fresh
        queryClient.setQueryData(turnKey, cancelledTurn, {
          updatedAt: Date.now() + 1000, // Set to future time to ensure it's considered fresh
        })
      }

      return { previousChatData, previousTurn }
    },

    onError: (error, { turnId }, context) => {
      console.error(error)
      toast.error('Failed to cancel turn')

      if (!context) return

      const turnKey = turnStatusKeyFactory({ workflowId, turnId, domainId })

      // Restore previous state
      queryClient.setQueryData<Turn>(turnKey, context.previousTurn)
      if (context.previousChatData && setChatData) {
        setChatData(context.previousChatData)
      }
    },

    onSuccess: () => {
      // The optimistic update should already be in place, no additional action needed
      // The query will stop refetching due to the CANCELLED status
    },
  })
}

export { Chat }
